import { useEffect, useMemo, useState } from 'react'
import { Search, RefreshCw, Loader2, Ribbon, ExternalLink } from 'lucide-react'
import { usersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

const getUserName = (u) => u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'
const getUserId   = (u) => u._id || u.id || ''

// Indian academic year from a raw year string / ISO date / "2023-24" etc.
function formatAcademicYear(value) {
  if (!value) return ''
  // Already formatted like "2023-24"
  if (/^\d{4}-\d{2,4}$/.test(String(value).trim())) return String(value).trim()
  // Numeric year → "2023-24"
  const y = parseInt(value, 10)
  if (!isNaN(y) && y > 1900) return `${y}-${String(y + 1).slice(-2)}`
  // ISO date → derive academic year (Apr-Mar)
  try {
    const d = new Date(value)
    if (!isNaN(d.getTime())) {
      const year = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1
      return `${year}-${String(year + 1).slice(-2)}`
    }
  } catch {}
  return String(value)
}

export default function AlumniPage() {
  const navigate = useNavigate()
  const [alumni, setAlumni]   = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [selectedYear, setSelectedYear] = useState('all')

  const fetchAlumni = async () => {
    try {
      setLoading(true)
      const data = await usersApi.getAll({ role: 'student', studentStatus: 'completed' })
      const list = Array.isArray(data) ? data : data?.users ?? []
      setAlumni(list)
    } catch {
      toast.error('Failed to load alumni')
      setAlumni([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAlumni() }, [])

  // Derive unique academic year options from alumni list
  const academicYearOptions = useMemo(() => {
    const years = [...new Set(alumni.map((a) => formatAcademicYear(a.completionAcademicYear)).filter(Boolean))]
    return ['all', ...years.sort((a, b) => b.localeCompare(a, 'en', { numeric: true }))]
  }, [alumni])

  // Filter and sort
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return [...alumni]
      .filter((a) => {
        const matchYear = selectedYear === 'all' || formatAcademicYear(a.completionAcademicYear) === selectedYear
        if (!matchYear) return false
        if (!q) return true
        return (
          getUserName(a).toLowerCase().includes(q) ||
          a.email?.toLowerCase().includes(q) ||
          a.mobile?.includes(q) ||
          a.admissionNo?.toLowerCase().includes(q)
        )
      })
      .sort((a, b) => {
        // Sort by completion year desc, then name asc
        const ay = formatAcademicYear(a.completionAcademicYear) || ''
        const by = formatAcademicYear(b.completionAcademicYear) || ''
        const byYear = by.localeCompare(ay, 'en', { numeric: true })
        if (byYear !== 0) return byYear
        return getUserName(a).localeCompare(getUserName(b), 'en', { sensitivity: 'base' })
      })
  }, [alumni, search, selectedYear])

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Alumni</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? '…' : `${alumni.length} completed student${alumni.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={fetchAlumni} className="btn-secondary p-2 self-start sm:self-auto">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Hero info card */}
      <div className="card p-5 flex items-center gap-4 border border-amber-100 bg-amber-50/60">
        <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Ribbon className="w-6 h-6 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900">Completed Students Archive</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Students who completed the highest class stay here for history, report access, and future reference.
          </p>
        </div>
        <div className="text-center flex-shrink-0">
          <p className="text-2xl font-extrabold text-amber-600">{loading ? '—' : alumni.length}</p>
          <p className="text-xs text-amber-600 font-medium">Alumni</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 flex-1">
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, admission no…"
            className="bg-transparent text-sm outline-none w-full"
          />
        </div>

        {/* Academic year filter */}
        {academicYearOptions.length > 1 && (
          <div className="flex gap-2 flex-wrap">
            {academicYearOptions.map((yr) => (
              <button
                key={yr}
                onClick={() => setSelectedYear(yr)}
                className={`text-xs font-semibold px-3 py-2 rounded-xl border transition-colors ${
                  selectedYear === yr
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {yr === 'all' ? 'All Years' : yr}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Alumni grid */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-100 rounded w-2/3" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Ribbon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-medium">
            {alumni.length === 0
              ? 'No alumni yet'
              : 'No alumni match your search'}
          </p>
          <p className="text-xs mt-1 text-gray-300">
            {alumni.length === 0
              ? 'Students who complete the highest class will appear here.'
              : 'Try adjusting your search or year filter.'}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => {
            const uid = getUserId(a)
            const name = getUserName(a)
            const compYear = formatAcademicYear(a.completionAcademicYear)
            const classLabel = a.classId?.name
              ? `${a.classId.name}${a.classId.section ? ` — ${a.classId.section}` : ''}`
              : a.className || ''

            return (
              <div key={uid} className="card p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <Avatar src={a.avatar} name={name} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{name}</p>
                    {a.admissionNo && (
                      <p className="text-xs text-gray-400">Adm. {a.admissionNo}</p>
                    )}
                    {classLabel && (
                      <p className="text-xs text-gray-400">{classLabel}</p>
                    )}
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between">
                  {compYear ? (
                    <span className="text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full">
                      Batch {compYear}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                  <button
                    onClick={() => navigate(`/users/${uid}`)}
                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View profile <ExternalLink className="w-3 h-3" />
                  </button>
                </div>

                {a.email && (
                  <p className="text-xs text-gray-400 mt-2 truncate">{a.email}</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
